import { useEffect, useMemo, useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { AlertCircle, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatWhen } from "@/lib/xeroRunFormat";
import {
  CCH_HISTORY_PAGE_SIZES,
  CCH_TRIGGER_LABEL,
  cchPipelineHistoryQueryKey,
  listCchPipelineRuns,
  type CchHistoryPageSize,
  type CchPipelineRun,
  type CchTriggerStatus,
} from "@/services/cchApi";

const TRIGGER_STYLES: Record<CchTriggerStatus, string> = {
  queued: "border-amber-200 bg-amber-50 text-amber-700",
  running: "border-sky-200 bg-sky-50 text-sky-700",
  completed: "border-emerald-200 bg-emerald-50 text-emerald-700",
  failed: "border-red-200 bg-red-50 text-red-700",
  cancelled: "border-border bg-muted text-muted-foreground",
  unknown: "border-border bg-muted text-muted-foreground",
};

interface Props {
  activeRunId: string | null;
  onSelect: (runId: string) => void;
}

const DEFAULT_PAGE_SIZE: CchHistoryPageSize = CCH_HISTORY_PAGE_SIZES[0];
const MAX_VISIBLE_PAGE_LINKS = 7;

export default function CchPipelineHistoryList({ activeRunId, onSelect }: Props) {
  const [pageSize, setPageSize] = useState<CchHistoryPageSize>(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(1);

  const skip = (page - 1) * pageSize;

  const history = useQuery({
    queryKey: cchPipelineHistoryQueryKey({
      limit: pageSize,
      skip,
      excludeInFlight: true,
    }),
    queryFn: () =>
      listCchPipelineRuns({
        limit: pageSize,
        skip,
        excludeInFlight: true,
      }),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });

  const total = history.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const pageLinks = useMemo(
    () => buildPageLinks(page, pageCount, MAX_VISIBLE_PAGE_LINKS),
    [page, pageCount],
  );

  const items = history.data?.items ?? [];
  const rangeStart = total === 0 ? 0 : skip + 1;
  const rangeEnd = Math.min(skip + items.length, total);

  return (
    <section className="space-y-3 rounded-lg border border-border p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="space-y-0.5">
          <h3 className="text-sm font-medium text-foreground">History</h3>
          <p className="text-xs text-muted-foreground">
            {total === 0
              ? "No past CCH pipeline runs recorded yet."
              : `Showing ${rangeStart}–${rangeEnd} of ${total.toLocaleString()} runs, newest first. Click a row to open its detail above.`}
          </p>
        </div>
        <PageSizeSelect
          value={pageSize}
          onChange={(next) => {
            setPageSize(next);
            setPage(1);
          }}
        />
      </div>

      {history.isPending ? (
        <div className="flex items-center justify-center gap-2 rounded-md border border-dashed border-border p-6 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading history…
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-md border border-dashed border-border p-6 text-center text-xs text-muted-foreground">
          No CCH pipeline runs on this page.
        </div>
      ) : (
        <ul className="divide-y divide-border rounded-md border border-border">
          {items.map((run) => (
            <HistoryRow
              key={run.run_id}
              run={run}
              isActive={run.run_id === activeRunId}
              onSelect={onSelect}
            />
          ))}
        </ul>
      )}

      {pageCount > 1 && (
        <Pagination className="mt-1 justify-end">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                onClick={(event) => {
                  event.preventDefault();
                  if (page > 1) setPage(page - 1);
                }}
                aria-disabled={page === 1}
                className={page === 1 ? "pointer-events-none opacity-50" : undefined}
              />
            </PaginationItem>
            {pageLinks.map((link, index) =>
              link === "ellipsis" ? (
                <PaginationItem key={`ellipsis-${index}`}>
                  <PaginationEllipsis />
                </PaginationItem>
              ) : (
                <PaginationItem key={link}>
                  <PaginationLink
                    href="#"
                    isActive={link === page}
                    onClick={(event) => {
                      event.preventDefault();
                      setPage(link);
                    }}
                  >
                    {link}
                  </PaginationLink>
                </PaginationItem>
              ),
            )}
            <PaginationItem>
              <PaginationNext
                href="#"
                onClick={(event) => {
                  event.preventDefault();
                  if (page < pageCount) setPage(page + 1);
                }}
                aria-disabled={page === pageCount}
                className={
                  page === pageCount
                    ? "pointer-events-none opacity-50"
                    : undefined
                }
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </section>
  );
}

function PageSizeSelect({
  value,
  onChange,
}: {
  value: CchHistoryPageSize;
  onChange: (next: CchHistoryPageSize) => void;
}) {
  return (
    <label className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
      <span className="whitespace-nowrap">Rows per page</span>
      <Select
        value={String(value)}
        onValueChange={(next) =>
          onChange(Number(next) as CchHistoryPageSize)
        }
      >
        <SelectTrigger className="h-8 w-[76px] text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {CCH_HISTORY_PAGE_SIZES.map((size) => (
            <SelectItem key={size} value={String(size)}>
              {size}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </label>
  );
}

function HistoryRow({
  run,
  isActive,
  onSelect,
}: {
  run: CchPipelineRun;
  isActive: boolean;
  onSelect: (runId: string) => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(run.run_id)}
        className={`flex w-full flex-col gap-1 px-3 py-2.5 text-left transition-colors ${
          isActive ? "bg-primary/5" : "hover:bg-accent/50"
        }`}
      >
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <Badge
            variant="outline"
            className={`font-normal ${TRIGGER_STYLES[run.status]}`}
          >
            {run.status}
          </Badge>
          <span className="text-sm font-medium text-foreground">
            {CCH_TRIGGER_LABEL[run.trigger]}
          </span>
          <span
            className="font-mono text-xs text-muted-foreground"
            title={run.run_id}
          >
            {shortRunId(run.run_id)}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span>Triggered {formatWhen(run.triggered_at)}</span>
          {run.triggered_by && (
            <>
              <span aria-hidden>·</span>
              <span className="truncate" title={run.triggered_by}>
                by {run.triggered_by}
              </span>
            </>
          )}
          {run.finished_at && (
            <>
              <span aria-hidden>·</span>
              <span>Finished {formatWhen(run.finished_at)}</span>
            </>
          )}
        </div>
        {run.failure_reason && (
          <div className="flex items-start gap-1.5 text-xs text-destructive">
            <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
            <span className="line-clamp-2">{run.failure_reason}</span>
          </div>
        )}
      </button>
    </li>
  );
}

function shortRunId(runId: string): string {
  const parts = runId.split("-");
  return parts.length > 1 ? parts[parts.length - 1] : runId;
}

function buildPageLinks(
  current: number,
  total: number,
  maxLinks: number,
): Array<number | "ellipsis"> {
  if (total <= maxLinks) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const first = 1;
  const last = total;
  
  const windowSize = Math.max(1, maxLinks - 4);
  const half = Math.floor(windowSize / 2);

  let start = Math.max(current - half, first + 1);
  let end = start + windowSize - 1;
  if (end >= last) {
    end = last - 1;
    start = Math.max(end - windowSize + 1, first + 1);
  }

  const links: Array<number | "ellipsis"> = [first];
  if (start > first + 1) links.push("ellipsis");
  for (let i = start; i <= end; i += 1) links.push(i);
  if (end < last - 1) links.push("ellipsis");
  links.push(last);
  return links;
}
